import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle, Menu, X, Lock, Globe, Award, ArrowRight, Mail, Check,
} from "lucide-react";
import { trackPageview } from "@/lib/vitrineAnalytics";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/lobzyet9_file_00000000236c71fdaa823d03db86abe9.png";
const FACEBOOK_URL = "https://www.facebook.com/share/1FLdQrdVRj/";
const WHATSAPP_URL = "https://wa.me/2250768127000";

const NAV_ITEMS = [
  { label: "Produits", to: "/#products" },
  { label: "Tarifs", to: "/tarifs" },
  { label: "Cas clients", to: "/cas-clients" },
  { label: "Institution", to: "/institution" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export function PublicHeader({ activeKey }) {
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  return (
    <header data-testid="public-header" className="fixed top-0 inset-x-0 z-50 bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-[#E5E4E2]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
          <img src={LOGO_URL} alt="CMES" className="h-9 w-9 object-contain" />
          <div className="leading-tight">
            <p className="font-serif text-[17px] tracking-tight text-[#0A0A0A] font-semibold">CMES Pharma Intel</p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#4A4D4C]">Institution officinale</p>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-[13px] transition-colors relative group ${activeKey === n.label ? "text-[#025946] font-semibold" : "text-[#0A0A0A]/75 font-medium hover:text-[#025946]"}`}>
              {n.label}
              <span className={`absolute -bottom-1 left-0 h-px bg-[#025946] transition-all duration-300 ${activeKey === n.label ? "w-full" : "w-0 group-hover:w-full"}`}></span>
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => nav("/login")} data-testid="header-login-btn" className="px-5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] hover:text-[#025946]">Espace Pharmacien</button>
          <button onClick={() => nav("/register")} data-testid="header-cta-btn" className="px-6 py-2.5 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[13px] font-semibold flex items-center gap-2 transition-colors">
            Essai 30 jours <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">{menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
      </div>
      {menuOpen && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-[#E5E4E2] px-6 py-6 space-y-4">
          {NAV_ITEMS.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} className="block text-sm font-medium">{n.label}</Link>
          ))}
          <button onClick={() => nav("/register")} className="w-full px-6 py-3 bg-[#0A0A0A] text-white text-sm font-semibold">Essai 30 jours</button>
        </div>
      )}
    </header>
  );
}

export function NewsletterForm({ compact = false }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND}/api/public/newsletter/subscribe`, { email, source: "landing_footer" });
      setDone(true);
      toast.success(res.data.already_subscribed ? "Vous êtes déjà abonné·e." : "Merci ! Bienvenue parmi nous.");
      setEmail("");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally { setLoading(false); }
  };

  if (done && !compact) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-[#C9A25E]">
        <Check className="w-4 h-4" /> Merci ! Confirmation envoyée par email.
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="newsletter-form" className={compact ? "flex gap-2" : "flex flex-col sm:flex-row gap-2 max-w-md"}>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="votre@email.pro" data-testid="newsletter-email"
        className="flex-1 px-4 py-3 bg-white/5 border border-white/15 text-white text-[13px] placeholder:text-white/30 focus:border-[#C9A25E] focus:outline-none transition-colors"
      />
      <button type="submit" disabled={loading} data-testid="newsletter-submit"
        className="px-6 py-3 bg-[#C9A25E] hover:bg-[#D4B06E] text-[#0A0A0A] text-[12px] font-bold uppercase tracking-wider transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        <Mail className="w-4 h-4" /> {loading ? "..." : "S'abonner"}
      </button>
    </form>
  );
}

export function PublicFooter() {
  return (
    <footer data-testid="public-footer" className="bg-[#0A0A0A] text-[#F9F8F6] pt-20 pb-10">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="CMES" className="h-10 w-10 object-contain" />
              <div>
                <p className="font-serif text-xl font-semibold">CMES Pharma Intel</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Institution officinale</p>
              </div>
            </div>
            <p className="text-[13px] text-white/60 leading-[1.7] max-w-sm mb-6">
              Le partenaire stratégique de santé publique des pharmaciens d’Afrique francophone. Abidjan, Côte d’Ivoire.
            </p>
            <div className="flex items-center gap-3">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C9A25E] hover:text-[#C9A25E] transition-colors">
                <MessageCircle className="w-4 h-4" />
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C9A25E] hover:text-[#C9A25E] transition-colors">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
            </div>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Plateforme</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li><Link to="/#products" className="hover:text-white transition-colors">Nos modules</Link></li>
              <li><Link to="/tarifs" className="hover:text-white transition-colors">Tarifs</Link></li>
              <li><Link to="/cas-clients" className="hover:text-white transition-colors">Cas clients</Link></li>
            </ul>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Institution</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li><Link to="/institution" className="hover:text-white transition-colors">Notre histoire</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/#trust" className="hover:text-white transition-colors">Conformité</Link></li>
            </ul>
          </div>

          <div className="col-span-12 lg:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-4">Newsletter</p>
            <p className="text-[12px] text-white/60 mb-4 leading-relaxed">Une lettre par mois. Études de cas, innovations réglementaires, conseils d'experts. Aucune publicité.</p>
            <NewsletterForm />
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Contact</p>
              <p className="text-[13px] text-white/70">
                <Link to="/contact" className="hover:text-white transition-colors">Écrivez-nous →</Link>
              </p>
              <p className="text-[12px] text-white/50 mt-1">innovationcmes@gmail.com</p>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-white/40">© 2026 CMES Pharma Intel. Institution privée de santé digitale.</p>
          <div className="flex items-center gap-6 text-[11px] text-white/40">
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> AES-256</span>
            <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Afrique</span>
            <span className="flex items-center gap-1.5"><Award className="w-3 h-3" /> Fait en Côte d’Ivoire</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function FloatingWhatsApp() {
  return (
    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-support-floating"
      className="fixed bottom-6 left-6 z-40 flex items-center gap-3 px-5 py-3 bg-[#025946] hover:bg-[#037A60] text-white shadow-2xl transition-all hover:scale-105">
      <MessageCircle className="w-4 h-4" />
      <span className="text-[12px] font-semibold hidden sm:inline">Parler à un expert · 7j/7</span>
    </a>
  );
}

export function PublicLayoutStyles() {
  return (
    <style>{`
      html { scroll-behavior: smooth; }
      ::selection { background: #025946; color: #F9F8F6; }
      .cmes-landing h1, .cmes-landing h2, .cmes-landing h3, .cmes-landing h4, .cmes-landing h5, .cmes-landing h6 {
        font-family: 'Cormorant Garamond', 'Georgia', serif;
        color: inherit;
        letter-spacing: -0.01em;
        font-weight: inherit;
      }
      .cmes-landing .text-white, .cmes-landing .text-white * { color: #FFFFFF !important; }
    `}</style>
  );
}
